/**
 * Host Peer Service
 * 
 * Service for managing peer-to-peer connections and communication from the Host's side
 */

import { IDisposer, IJsonPatch } from "mobx-state-tree";
import { webRTCService } from "./webrtcService";
import { consts } from "../consts";
import { appSyncStore } from "../stores/appSyncStore";
import { gameLoaderService } from "./gameLoaderService";
import { reaction } from "mobx";


export class HostPeerService {
    private _gamePatchListenerDisposer: IDisposer | null = null;

    private _participantsPeersLoadedGame: Set<string> = new Set<string>();

    private _peerIdToUserIdMap: Map<string, string> = new Map<string, string>();

    constructor() {
        webRTCService.onDataChannelOpen(this.handleOnDataChannelOpen.bind(this));
        webRTCService.onMessageReceived(this.handleOnMessageReceived.bind(this));
        appSyncStore.registerPatchListener(this.handleOnAppSharedPatch.bind(this));

        reaction(
            () => gameLoaderService.state,
            (state) => {
                this._participantsPeersLoadedGame.clear();
                if (this._gamePatchListenerDisposer) {
                    this._gamePatchListenerDisposer();
                    this._gamePatchListenerDisposer = null;
                }

                if (state != "loaded") return;

                for (const peerId of this._participantsPeersLoadedGame) {
                    this.sendGameSnapshotToPeerIfLocalGameLoaded(peerId);
                }

                this._gamePatchListenerDisposer = gameLoaderService.game?.gameSyncStore.registerPatchListener(this.handleOnGamePatch.bind(this)) ?? null;
            }
        );
    }

    handleOnDataChannelOpen(peerId: string, channel: RTCDataChannel) {
        console.log('[HostPeerService] New data channel for peer:', peerId, 'Channel state:', channel.readyState);

        channel
        webRTCService.sendToPeer(peerId, {
            type: consts.HOST_INFO,
        });

        const appSnapshot = appSyncStore.getSnapshotWithoutAvatars();
        webRTCService.sendToPeer(peerId, {
            type: consts.APP_SNAPSHOT,
            data: appSnapshot,
        });

        appSyncStore.users.forEach((user, id) => {
            // Skip test users - don't send their avatars over WebRTC
            if (user.isTestUser) {
                return;
            }
            webRTCService.sendToPeer(peerId, {
                type: consts.APP_AVATAR,
                data: { id: id, avatar: user.avatar }
            });
        });
    }

    handleOnMessageReceived(peerId: string, data: any) {
        switch (data.type) {

            // ********** USER INFO ************
            case consts.USER_INFO:
                const d = data.data;
                if (!d) {
                    console.error('No user info data received from peer', peerId);
                    return;
                }
                if (!d.id) {
                    console.error('No user ID received from peer', peerId);
                    return;
                }
                this._peerIdToUserIdMap.set(peerId, d.id);
                appSyncStore.upsertUser({
                    id: d.id,
                    name: d.name ?? 'Unknown',
                    avatar: d.avatar ? d.avatar : null,
                    isTestUser: false
                });

                // Send avatar to all peers except for the new User
                webRTCService.broadcastExcept(peerId, {
                    type: consts.APP_AVATAR,
                    data: { id: d.id, avatar: d.avatar }
                });

                // TODO: This can possibly be removed with a preProcessor on the UsersModel
                // Tell the new peer to use their own avatar for this ID
                webRTCService.sendToPeer(peerId, {
                    type: consts.APP_AVATAR_SELF,
                    data: { id: d.id }
                });
                break;

            // ********** GAME LOADED ************
            case consts.GAME_LOADED:
                this._participantsPeersLoadedGame.add(peerId);
                this.sendGameSnapshotToPeerIfLocalGameLoaded(peerId);
                break;


            case consts.GAME_MESSAGE:
                const game = gameLoaderService.game;
                if (game) {
                    const userId = this._peerIdToUserIdMap.get(peerId);
                    if (userId) {
                        game.gameRpcToHost.runOnHost(userId, data.data);
                    } else {
                        console.error('User ID not found for peer', peerId);
                    }
                } else {
                    console.error('Game not loaded yet, cannot process Game message');
                }
                break;
        }
    }

    private sendGameSnapshotToPeerIfLocalGameLoaded(peerId: string): void {
        const game = gameLoaderService.game;
        if (game) {
            const snapshot = game.gameSyncStore.getSnapshot();
            // console.log('[HostPeerService] Sending GAME_SNAPSHOT to peer:', peerId, 'Snapshot:', snapshot);
            webRTCService.sendToPeer(peerId, {
                type: consts.GAME_SNAPSHOT,
                data: snapshot,
            });
        }
    }

    handleOnAppSharedPatch = (patch: IJsonPatch, reversePatch: IJsonPatch) => {
        webRTCService.broadcast({
            type: consts.APP_PATCH,
            data: patch,
        });
    }

    handleOnGamePatch = (patch: IJsonPatch, reversePatch: IJsonPatch) => {
        webRTCService.broadcast({
            type: consts.GAME_PATCH,
            data: patch,
        });
    }

    /**
     * Disconnect a specific participant by user ID
     * @param userId The ID of the user to disconnect
     * @returns true if the user was found and disconnected, false otherwise
     */
    public disconnectParticipant(userId: string): boolean {
        // Find peer ID by user ID
        const targetPeerId = Array.from(this._peerIdToUserIdMap.entries())
            .find(([_, mappedUserId]) => mappedUserId === userId)?.[0];

        if (targetPeerId) {
            // Close the WebRTC connection
            webRTCService.closePeer(targetPeerId);
            
            // Remove from peer map
            this._peerIdToUserIdMap.delete(targetPeerId);
            
            return true;
        }

        return false;
    }
}
